import React, { useId, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';

const FILTER_THRESHOLD = 8;

/**
 * Plural helper.
 * @param {number} count - Count.
 * @param {string} word - Singular word.
 * @returns {string} "1 word" / "2 words".
 */
export const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Checklist of evidence items with select-all, an optional filter and a selected count.
 * @param {object} props - Props.
 * @param {string} props.title - Section title (e.g. "Requirements").
 * @param {string} props.noun - Singular noun for counts.
 * @param {Array<{id: string, primary: string, secondary?: string, chips?: string[]}>} props.items - Items.
 * @param {Set<string>} props.selected - Selected ids.
 * @param {function(Set<string>): void} props.onChange - New selection.
 * @param {string} props.emptyText - Shown when there are no items.
 * @param {string} [props.helper] - Helper text under the title.
 * @returns {JSX.Element} Checklist.
 */
function SourceChecklist({ title, noun, items, selected, onChange, emptyText, helper }) {
  const [query, setQuery] = useState('');
  const headingId = useId();
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => `${item.primary} ${item.secondary || ''}`.toLowerCase().includes(q));
  }, [items, query]);

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-xl)', bgcolor: 'background.paper', overflow: 'hidden', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: items.length ? '1px solid' : 0, borderColor: 'var(--q-border-subtle)' }}>
        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography id={headingId} component="h3" sx={{ fontSize: 14, fontWeight: 600 }}>
            {title}
            <Typography component="span" sx={{ ml: 1, fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
              {items.length ? `${selected.size} of ${items.length} selected` : '0'}
            </Typography>
          </Typography>
          {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
        </Box>
        {items.length > 0 && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onChange(allSelected ? new Set() : new Set(items.map((i) => i.id)))}
              />
            )}
            label={<Typography variant="body2">Select all</Typography>}
          />
        )}
      </Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>{emptyText}</Typography>
      ) : (
        <>
          {items.length > FILTER_THRESHOLD && (
            <Box sx={{ px: 2, pt: 1.25 }}>
              <TextField
                fullWidth
                placeholder={`Filter ${noun}s`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                inputProps={{ 'aria-label': `Filter ${noun}s` }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Box>
          )}
          <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto', py: 0.5 }} aria-label={title}>
            {visible.map((item) => {
              const checked = selected.has(item.id);
              const labelId = `${headingId}-${item.id}`;
              return (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton onClick={() => toggle(item.id)} sx={{ alignItems: 'flex-start', py: 0.75, px: 2 }}>
                    <ListItemIcon sx={{ minWidth: 34, mt: -0.5 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={item.primary}
                      secondary={item.secondary}
                      primaryTypographyProps={{ sx: { fontSize: 13.5, fontWeight: 500, overflowWrap: 'anywhere' } }}
                      secondaryTypographyProps={{ sx: { display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' } }}
                    />
                    {item.chips?.length > 0 && (
                      <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, ml: 1, flex: 'none' }}>
                        {item.chips.map((chip) => <Chip key={chip} size="small" label={chip} />)}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
            {visible.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>Nothing matches that filter.</Typography>
            )}
          </List>
        </>
      )}
    </Box>
  );
}

/**
 * Step 2: choose the project and the evidence (requirements, sessions or a reference document).
 * @param {object} props - Props.
 * @param {Array<object>} props.projects - Projects to offer (with customer, requirements, sessions, generatedDocuments).
 * @param {object|null} props.project - Selected project.
 * @param {function(string): void} props.onProjectChange - Select a project by id ('' clears).
 * @param {{id: string, name: string}|null} props.customerFilter - Customer the list is limited to.
 * @param {function(): void} props.onClearCustomerFilter - Show all customers.
 * @param {number} props.fileCount - Uploaded supporting files for the project.
 * @param {Set<string>} props.requirementIds - Selected requirement ids.
 * @param {function(Set<string>): void} props.onRequirementIdsChange - Update selection.
 * @param {Set<string>} props.sessionIds - Selected session ids.
 * @param {function(Set<string>): void} props.onSessionIdsChange - Update selection.
 * @param {'requirements'|'existing_document'} props.contentSource - Evidence mode.
 * @param {function(string): void} props.onContentSourceChange - Change evidence mode.
 * @param {Array<object>} props.existingDocuments - Documents usable as a reference.
 * @param {string} props.existingDocumentId - Selected reference document.
 * @param {function(string): void} props.onExistingDocumentChange - Select a reference document.
 * @param {boolean} props.isPresentation - Selected template is a deck.
 * @returns {JSX.Element} Step body.
 */
export default function SourcesStep({
  projects,
  project,
  onProjectChange,
  customerFilter,
  onClearCustomerFilter,
  fileCount,
  requirementIds,
  onRequirementIdsChange,
  sessionIds,
  onSessionIdsChange,
  contentSource,
  onContentSourceChange,
  existingDocuments,
  existingDocumentId,
  onExistingDocumentChange,
  isPresentation,
}) {
  const options = useMemo(
    () => [...projects].sort((a, b) =>
      (a.customer?.name || '').localeCompare(b.customer?.name || '') || (a.name || '').localeCompare(b.name || '')),
    [projects]
  );

  const requirementItems = useMemo(() => (project?.requirements || []).map((r) => ({
    id: r.id,
    primary: r.title || 'Untitled requirement',
    secondary: r.description || '',
    chips: [r.type, r.priority].filter(Boolean),
  })), [project]);

  const sessionItems = useMemo(() => (project?.sessions || []).map((s) => ({
    id: s.id,
    primary: s.name || 'Untitled session',
    secondary: [formatDate(s.createdAt), s.summary ? s.summary.replace(/\s+/g, ' ').slice(0, 160) : 'No summary yet'].join(' · '),
  })), [project]);

  const hasSources = (p) => (p.requirements?.length || 0) + (p.sessions?.length || 0) + (p.generatedDocuments?.length || 0) > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {customerFilter && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">Showing projects for</Typography>
            <Chip size="small" label={customerFilter.name} onDelete={onClearCustomerFilter} />
          </Box>
        )}
        <Autocomplete
          options={options}
          value={project || null}
          onChange={(e, next) => onProjectChange(next?.id || '')}
          groupBy={(option) => option.customer?.name || 'No customer'}
          getOptionLabel={(option) => option.name || 'Untitled project'}
          getOptionDisabled={(option) => !hasSources(option)}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hasSources(option)
                      ? `${plural(option.requirements?.length || 0, 'requirement')} · ${plural(option.sessions?.length || 0, 'session')}`
                      : 'No sessions or requirements yet'}
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => <TextField {...params} label="Project" placeholder="Search projects" />}
          noOptionsText="No projects found"
        />
        {projects.length === 0 && (
          <Alert severity="info">Create a project and add a session or requirements to generate documents from.</Alert>
        )}
      </Box>

      {project && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip size="small" color="primary" label={plural(project.requirements?.length || 0, 'requirement')} />
            <Chip size="small" label={plural(project.sessions?.length || 0, 'session')} />
            <Chip size="small" label={plural(project.generatedDocuments?.length || 0, 'document')} />
            <Chip size="small" label={plural(fileCount, 'file')} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600 }}>Evidence</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={contentSource}
              onChange={(e, next) => next && onContentSourceChange(next)}
              aria-label="Evidence"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="requirements">Requirements and sessions</ToggleButton>
              <ToggleButton value="existing_document" disabled={existingDocuments.length === 0}>
                Use an existing document
              </ToggleButton>
            </ToggleButtonGroup>
            {isPresentation && (
              <Typography variant="caption" color="text.secondary">
                {existingDocuments.length > 0
                  ? 'For decks, an already generated Word document usually gives the most consistent slides.'
                  : 'For decks, a generated Word document usually gives the most consistent slides. This project has none yet, so the deck is built from requirements and sessions.'}
              </Typography>
            )}
          </Box>

          {contentSource === 'existing_document' && (
            <FormControl fullWidth>
              <InputLabel id="generator-existing-doc-label">Reference document</InputLabel>
              <Select
                labelId="generator-existing-doc-label"
                value={existingDocumentId}
                label="Reference document"
                onChange={(e) => onExistingDocumentChange(e.target.value)}
              >
                {existingDocuments.map((doc) => (
                  <MenuItem key={doc.id} value={doc.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5 }}>{doc.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.templateName || 'No template'} · {formatDate(doc.createdAt)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <SourceChecklist
              title="Requirements"
              noun="requirement"
              items={requirementItems}
              selected={requirementIds}
              onChange={onRequirementIdsChange}
              emptyText="This project has no requirements yet. Extract them from a session for better results."
            />
            <SourceChecklist
              title="Sessions"
              noun="session"
              items={sessionItems}
              selected={sessionIds}
              onChange={onSessionIdsChange}
              emptyText="No sessions in this project."
              helper="Summaries and action items are used as evidence."
            />
          </Box>
        </>
      )}
    </Box>
  );
}
